import { X, Settings2, Terminal, Eye, EyeOff, Clock, FileText, MessageSquare, ThumbsUp, ThumbsDown, BookOpen, ChevronDown, ChevronRight, FileCode, Database, Users, HelpCircle, Server, RefreshCw, Sparkles, Pencil, Trash2 } from 'lucide-react';
import { useState, useEffect, useRef, Fragment } from 'react';
import { apiService, promptService } from '../../services';
import { PROMPT_TYPES } from '../../services/prompts/constants';
import { Dialog, Transition } from '@headlessui/react'
import JsonView from '@microlink/react-json-view';
import ReactMarkdown from 'react-markdown';

const PromptsSection = () => {
  const [activePrompt, setActivePrompt] = useState(PROMPT_TYPES.SYSTEM);
  const [prompts, setPrompts] = useState(() => promptService.getAllPrompts());
  const [isResetting, setIsResetting] = useState(false);

  const handlePromptChange = (value) => {
    setPrompts(prev => ({ ...prev, [activePrompt]: value }));
    promptService.setPrompt(activePrompt, value);
  };

  const handleReset = async () => {
    try {
      setIsResetting(true);
      const newPrompts = await promptService.resetPrompts();
      
      // Check if we got valid prompts back
      if (newPrompts && typeof newPrompts === 'object' && 
          (newPrompts[PROMPT_TYPES.SYSTEM] || newPrompts.system)) {
        setPrompts(newPrompts);
        // Optional: Add toast notification for success
        // toast.success('Prompts have been reset successfully');
      } else {
        // Fallback to current prompts if reset fails
        const currentPrompts = promptService.getAllPrompts();
        setPrompts(currentPrompts);
        // Optional: Add toast notification for error
        // toast.error('Failed to reset prompts. Using current values.');
      }
    } catch (error) {
      // Fallback to current prompts if reset fails
      const currentPrompts = promptService.getAllPrompts();
      setPrompts(currentPrompts);
      // Optional: Add toast notification for error
      // toast.error(`Failed to reset prompts: ${error.message}`);
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="p-6">
      <h3 className="text-lg font-medium mb-6">Instructions</h3>
      
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {Object.entries(PROMPT_TYPES)
            .filter(([key]) => key !== 'USER') // Filter out USER prompt type
            .map(([key, value]) => (
              <button
                key={value}
                onClick={() => setActivePrompt(value)}
                className={`
                  whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm
                  ${activePrompt === value
                    ? 'border-gray-600 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {key.charAt(0) + key.slice(1).toLowerCase()}
              </button>
            ))}
        </nav>
      </div>

      <div className="space-y-4">
        <textarea
          value={prompts[activePrompt]}
          onChange={(e) => handlePromptChange(e.target.value)}
          className="w-full h-96 font-mono text-sm p-4 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-500 focus:outline-none"
          spellCheck={false}
        />
        
        <button
          onClick={handleReset}
          className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md border border-gray-300"
        >
          Reset to Default
        </button>
      </div>
    </div>
  )
}

// Feedback Section Component - fetches feedback from API
const FeedbackSection = () => {
  const [feedbackList, setFeedbackList] = useState([]);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'archived'
  const [isLoading, setIsLoading] = useState(true);
  const [isClearing, setIsClearing] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizeResult, setOptimizeResult] = useState(null);
  const [showOptimizePopup, setShowOptimizePopup] = useState(false);
  const [error, setError] = useState(null);

  // Fetch feedback when tab changes
  const loadFeedback = async (archived = false) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.listFeedback({ limit: 100, archived });
      setFeedbackList(response.feedback || []);
    } catch (err) {
      console.error('[FeedbackSection] Failed to load feedback:', err);
      setError('Failed to load feedback');
      setFeedbackList([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch feedback on mount and tab change
  useEffect(() => {
    loadFeedback(activeTab === 'archived');
  }, [activeTab]);

  const handleClearFeedback = async () => {
    if (!confirm('Are you sure you want to clear all feedback? This action cannot be undone.')) {
      return;
    }

    try {
      setIsClearing(true);
      setError(null);
      await apiService.clearFeedback();
      setFeedbackList([]);
    } catch (err) {
      console.error('[FeedbackSection] Failed to clear feedback:', err);
      setError('Failed to clear feedback');
    } finally {
      setIsClearing(false);
    }
  };

  const handleDeleteFeedback = async (feedbackId) => {
    try {
      setError(null);
      await apiService.deleteFeedback(feedbackId);
      setFeedbackList(prev => prev.filter(item => item.id !== feedbackId));
    } catch (err) {
      console.error('[FeedbackSection] Failed to delete feedback:', err);
      setError('Failed to delete feedback');
    }
  };

  const handleOptimize = async () => {
    // Show popup immediately with loading state
    setShowOptimizePopup(true);
    setOptimizeResult(null);
    setIsOptimizing(true);
    setError(null);

    try {
      const result = await apiService.generateOptimizedContext();
      setOptimizeResult(result);
      // Refresh the feedback list since items may have been archived
      loadFeedback(activeTab === 'archived');
    } catch (err) {
      console.error('[FeedbackSection] Failed to optimize:', err);
      setError('Failed to generate optimized context');
      setShowOptimizePopup(false);
    } finally {
      setIsOptimizing(false);
    }
  };

  // Sort feedback by newest first
  const sortedFeedback = [...feedbackList].sort((a, b) => {
    const dateA = new Date(a.createdAt || a.dateCreated);
    const dateB = new Date(b.createdAt || b.dateCreated);
    return dateB - dateA;
  });

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">User Feedback</h3>
        <div className="flex items-center space-x-3">
          {activeTab === 'pending' && (
            <>
              <button
                onClick={handleOptimize}
                disabled={isOptimizing || isLoading || feedbackList.length === 0}
                className="px-3 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isOptimizing ? 'Optimizing...' : 'Optimize Model'}
              </button>
              <button
                onClick={handleClearFeedback}
                disabled={isClearing || isLoading || feedbackList.length === 0}
                className="px-3 py-1.5 text-sm text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isClearing ? 'Clearing...' : 'Clear All'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('pending')}
            className={`whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'pending'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setActiveTab('archived')}
            className={`whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'archived'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Archived
          </button>
        </nav>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="text-center text-gray-500 py-8">
            Loading feedback...
          </div>
        ) : sortedFeedback.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            {activeTab === 'pending'
              ? 'No pending feedback. Submit feedback on chat responses to help improve the AI.'
              : 'No archived feedback yet. Feedback is archived after optimization.'}
          </div>
        ) : (
          <div className="space-y-4">
            {sortedFeedback.map((item, index) => (
              <div key={item.id || index} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center flex-wrap gap-2">
                    {item.feedbackType === 'positive' ? (
                      <ThumbsUp className="h-4 w-4 text-green-500" />
                    ) : (
                      <ThumbsDown className="h-4 w-4 text-red-500" />
                    )}
                    <span className="text-sm text-gray-500">
                      {new Date(item.createdAt || item.dateCreated).toLocaleString()}
                    </span>
                    {item.archived && item.archivedAt && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                        Optimized {new Date(item.archivedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleDeleteFeedback(item.id)}
                    className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Delete feedback"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-sm text-gray-700 mb-2">{item.reason}</p>
                {item.question && (
                  <p className="text-xs text-gray-500 mb-2 italic">Q: {item.question}</p>
                )}
                <div className="mt-2 space-x-4 flex">
                  <details className="text-xs">
                    <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                      View Response JSON
                    </summary>
                    <div className="fixed inset-0 bg-black/30 z-[1010]" onClick={(e) => {
                      e.currentTarget.parentElement.removeAttribute('open');
                    }}>
                      <div
                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-2xl bg-white rounded-lg shadow-lg border"
                        onClick={e => e.stopPropagation()}
                        style={{ zIndex: 1011 }}
                      >
                        <div className="flex justify-between items-center p-4 border-b">
                          <h4 className="font-medium">Response Data</h4>
                          <button
                            onClick={(e) => {
                              e.currentTarget.closest('details').removeAttribute('open');
                            }}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                        <div className="p-4 max-h-[70vh] overflow-y-auto">
                          <JsonView
                            src={item.responseData || {}}
                            name={false}
                            collapsed={2}
                            enableClipboard={true}
                            displayDataTypes={false}
                            style={{
                              background: 'transparent',
                              fontSize: '12px',
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
                            }}
                            theme={{
                              base00: 'white',
                              base01: '#f5f5f5',
                              base02: '#e5e5e5',
                              base03: '#969696',
                              base04: '#666666',
                              base05: '#333333',
                              base06: '#252525',
                              base07: '#000000',
                              base08: '#ff5555',
                              base09: '#ff9955',
                              base0A: '#ffdd55',
                              base0B: '#55ff55',
                              base0C: '#55ffff',
                              base0D: '#5555ff',
                              base0E: '#ff55ff',
                              base0F: '#ff5555'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </details>

                  <details className="text-xs">
                    <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                      View SQL Query
                    </summary>
                    <div className="fixed inset-0 bg-black/30 z-[1010]" onClick={(e) => {
                      e.currentTarget.parentElement.removeAttribute('open');
                    }}>
                      <div
                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-2xl bg-white rounded-lg shadow-lg border"
                        onClick={e => e.stopPropagation()}
                        style={{ zIndex: 1011 }}
                      >
                        <div className="flex justify-between items-center p-4 border-b">
                          <h4 className="font-medium">SQL Query</h4>
                          <button
                            onClick={(e) => {
                              e.currentTarget.closest('details').removeAttribute('open');
                            }}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                        <div className="p-4 max-h-[70vh] overflow-y-auto">
                          <pre className="text-sm whitespace-pre-wrap bg-gray-50 p-4 rounded-md font-mono">
                            {item.sqlQuery || item.responseData?.sql || 'No SQL query available'}
                          </pre>
                        </div>
                      </div>
                    </div>
                  </details>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Optimize Result Popup */}
      {showOptimizePopup && (
        <div className="fixed inset-0 bg-black/50 z-[1020] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-medium">
                {isOptimizing ? 'Optimizing Model...' : 'Optimization Result'}
              </h3>
              {!isOptimizing && (
                <button
                  onClick={() => setShowOptimizePopup(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
            <div className="p-4 flex-1 overflow-y-auto">
              {/* Loading state with progress bar */}
              {isOptimizing && (
                <div className="py-12">
                  <div className="flex flex-col items-center gap-4">
                    <Sparkles className="h-12 w-12 text-blue-500 animate-pulse" />
                    <p className="text-sm text-gray-600">Analyzing feedback and generating optimizations...</p>
                    <div className="w-full max-w-md h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full animate-[progress_2s_ease-in-out_infinite]"
                        style={{
                          width: '30%',
                          animation: 'progress 2s ease-in-out infinite'
                        }}
                      />
                    </div>
                  </div>
                  <style>{`
                    @keyframes progress {
                      0% { transform: translateX(-100%); width: 30%; }
                      50% { transform: translateX(150%); width: 50%; }
                      100% { transform: translateX(400%); width: 30%; }
                    }
                  `}</style>
                </div>
              )}

              {/* Results content */}
              {!isOptimizing && (
                <>
                  {/* User-friendly summary with changes */}
                  {optimizeResult?.summary?.userSummary && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm text-green-800">{optimizeResult.summary.userSummary}</p>

                      {/* Changes list inside the summary box */}
                      {optimizeResult?.summary?.changes && optimizeResult.summary.changes.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-green-200">
                          <h4 className="font-medium text-sm text-green-800 mb-2">Changes Made:</h4>
                          <ul className="list-disc list-inside space-y-1 text-sm text-green-700">
                            {optimizeResult.summary.changes.map((change, idx) => (
                              <li key={idx}>{change}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Feedback stats */}
                  {optimizeResult?.summary?.feedbackAnalyzed && (
                    <div className="mb-4 flex gap-4 text-sm">
                      <span className="text-gray-600">
                        Feedback analyzed: <strong>{optimizeResult.summary.feedbackAnalyzed.total}</strong>
                      </span>
                      <span className="text-green-600">
                        Positive: <strong>{optimizeResult.summary.feedbackAnalyzed.positive}</strong>
                      </span>
                      <span className="text-red-600">
                        Negative: <strong>{optimizeResult.summary.feedbackAnalyzed.negative}</strong>
                      </span>
                    </div>
                  )}

                  {/* Detailed reasoning (collapsible) */}
                  {optimizeResult?.summary?.reasoning && (
                    <details className="mb-4">
                      <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                        Technical Details
                      </summary>
                      <p className="mt-2 text-sm text-gray-600 pl-4 border-l-2 border-gray-200">
                        {optimizeResult.summary.reasoning}
                      </p>
                    </details>
                  )}

                  {/* Full content (collapsible) */}
                  {optimizeResult?.content && (
                    <details>
                      <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                        View Full Optimization Content
                      </summary>
                      <div className="mt-2 prose prose-sm max-w-none">
                        <pre className="whitespace-pre-wrap bg-gray-50 p-4 rounded-md text-xs font-mono overflow-x-auto max-h-64">
                          {optimizeResult.content}
                        </pre>
                      </div>
                    </details>
                  )}

                  {!optimizeResult?.content && !optimizeResult?.summary && (
                    <div className="text-center text-gray-500 py-8">
                      No optimization content generated
                    </div>
                  )}
                </>
              )}
            </div>
            {!isOptimizing && (
              <div className="flex justify-end p-4 border-t">
                <button
                  onClick={() => setShowOptimizePopup(false)}
                  className="px-4 py-2 text-sm text-white bg-gray-600 hover:bg-gray-700 rounded-md"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Category icons and colors for Knowledge section
const categoryConfig = {
  content: { icon: FileText, color: 'bg-blue-100 text-blue-600', label: 'Content' },
  tables: { icon: Database, color: 'bg-green-100 text-green-600', label: 'Tables' },
  customer: { icon: Users, color: 'bg-purple-100 text-purple-600', label: 'Customer' },
  questions: { icon: HelpCircle, color: 'bg-amber-100 text-amber-600', label: 'Questions' },
  tidb: { icon: Server, color: 'bg-red-100 text-red-600', label: 'TiDB' }
};

// Training Section Component - Data, Interpretations, and Custom Context tabs
const TrainingSection = () => {
  const [activeTab, setActiveTab] = useState('data');
  const [viewMode, setViewMode] = useState('preview'); // 'edit' or 'preview'
  const [dataContent, setDataContent] = useState('');
  const [interpretationsContent, setInterpretationsContent] = useState('');
  const [customContextContent, setCustomContextContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Fetch all contexts on mount
  useEffect(() => {
    const loadContent = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const [dataResponse, interpretationsResponse, customResponse] = await Promise.all([
          apiService.getOptimizedContext('sql'),
          apiService.getOptimizedContext('interpretation'),
          apiService.getCustomContext()
        ]);
        // API returns nested structure: response.sql.content for SQL, response.interpretation.content for interpretation
        setDataContent(dataResponse?.sql?.content || dataResponse?.content || '');
        setInterpretationsContent(interpretationsResponse?.interpretation?.content || interpretationsResponse?.content || '');
        setCustomContextContent(customResponse.content || '');
      } catch (err) {
        console.error('[TrainingSection] Failed to load content:', err);
        setError('Failed to load training content');
      } finally {
        setIsLoading(false);
      }
    };
    loadContent();
  }, []);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccessMessage(null);

      if (activeTab === 'data') {
        await apiService.saveOptimizedContext(dataContent, 'sql');
        setSuccessMessage('Data optimizations saved successfully');
      } else if (activeTab === 'interpretations') {
        await apiService.saveOptimizedContext(interpretationsContent, 'interpretation');
        setSuccessMessage('Interpretation optimizations saved successfully');
      } else {
        await apiService.saveCustomContext(customContextContent);
        setSuccessMessage('Custom context saved successfully');
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('[TrainingSection] Failed to save:', err);
      const tabNames = { data: 'data optimizations', interpretations: 'interpretation optimizations', custom: 'custom context' };
      setError(`Failed to save ${tabNames[activeTab] || 'content'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCustomContext = async () => {
    if (!confirm('Are you sure you want to delete the custom context? This action cannot be undone.')) {
      return;
    }

    try {
      setIsDeleting(true);
      setError(null);
      await apiService.deleteCustomContext();
      setCustomContextContent('');
      setSuccessMessage('Custom context deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('[TrainingSection] Failed to delete custom context:', err);
      setError('Failed to delete custom context');
    } finally {
      setIsDeleting(false);
    }
  };

  const getCurrentContent = () => {
    if (activeTab === 'data') return dataContent;
    if (activeTab === 'interpretations') return interpretationsContent;
    return customContextContent;
  };
  const setCurrentContent = (value) => {
    if (activeTab === 'data') setDataContent(value);
    else if (activeTab === 'interpretations') setInterpretationsContent(value);
    else setCustomContextContent(value);
  };
  const currentContent = getCurrentContent();

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Training</h3>
        {/* Edit/Preview Toggle */}
        <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
          <button
            onClick={() => setViewMode('edit')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm ${
              viewMode === 'edit'
                ? 'bg-gray-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </button>
          <button
            onClick={() => setViewMode('preview')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm ${
              viewMode === 'preview'
                ? 'bg-gray-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Eye className="h-3.5 w-3.5" />
            Preview
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('data')}
            className={`whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'data'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Data
          </button>
          <button
            onClick={() => setActiveTab('interpretations')}
            className={`whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'interpretations'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Interpretations
          </button>
          <button
            onClick={() => setActiveTab('custom')}
            className={`whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'custom'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Custom Context
          </button>
        </nav>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-600">
          {successMessage}
        </div>
      )}

      {/* Content Area */}
      <div className="flex-1 flex flex-col min-h-0">
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            Loading...
          </div>
        ) : (
          <>
            <div className="text-xs text-gray-500 mb-2">
              {activeTab === 'data'
                ? 'AI-generated data optimizations based on user feedback. You can edit and save.'
                : activeTab === 'interpretations'
                ? 'AI-generated interpretation optimizations based on user feedback. You can edit and save.'
                : 'Add your own custom context in Markdown format.'}
            </div>
            {viewMode === 'edit' ? (
              <textarea
                value={currentContent}
                onChange={(e) => setCurrentContent(e.target.value)}
                className="flex-1 w-full font-mono text-sm p-4 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:outline-none resize-none"
                placeholder={activeTab === 'data'
                  ? '# Data Optimizations\n\nNo data optimizations generated yet. Use the "Optimize Model" button in the Feedback section to generate optimizations from user feedback.'
                  : activeTab === 'interpretations'
                  ? '# Interpretation Optimizations\n\nNo interpretation optimizations generated yet. Use the "Optimize Model" button in the Feedback section to generate optimizations from user feedback.'
                  : '# Custom Context\n\nAdd your custom context here in Markdown format...'}
                spellCheck={false}
              />
            ) : (
              <div className="flex-1 w-full p-4 border border-gray-300 rounded-md overflow-y-auto bg-white">
                {currentContent ? (
                  <div className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-strong:text-gray-900 prose-code:text-blue-600 prose-code:bg-blue-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-gray-50 prose-pre:border prose-pre:border-gray-200">
                    <ReactMarkdown>{currentContent}</ReactMarkdown>
                  </div>
                ) : (
                  <div className="text-gray-400 italic">
                    {activeTab === 'data'
                      ? 'No data optimizations generated yet. Use the "Optimize Model" button in the Feedback section.'
                      : activeTab === 'interpretations'
                      ? 'No interpretation optimizations generated yet. Use the "Optimize Model" button in the Feedback section.'
                      : 'No custom context added yet. Switch to Edit mode to add content.'}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end mt-4 space-x-3">
        {activeTab === 'custom' && customContextContent && (
          <button
            onClick={handleDeleteCustomContext}
            disabled={isDeleting || isLoading}
            className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        )}
        <button
          onClick={handleSave}
          disabled={isSaving || isLoading}
          className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

const ContextsSection = () => {
  const [contexts, setContexts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedFiles, setExpandedFiles] = useState({});

  const fetchContexts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.getContexts();
      setContexts(response.contexts || []);
      // Auto-expand first category
      const categories = [...new Set((response.contexts || []).map(c => c.category))];
      if (categories.length > 0) {
        setExpandedCategories({ [categories[0]]: true });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContexts();
  }, []);

  // Group contexts by category
  const groupedContexts = contexts.reduce((acc, ctx) => {
    if (!acc[ctx.category]) {
      acc[ctx.category] = [];
    }
    acc[ctx.category].push(ctx);
    return acc;
  }, {});

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleFile = (fileKey) => {
    setExpandedFiles(prev => ({
      ...prev,
      [fileKey]: !prev[fileKey]
    }));
  };

  const getCategoryConfig = (category) => {
    return categoryConfig[category] || {
      icon: FileCode,
      color: 'bg-gray-100 text-gray-600',
      label: category.charAt(0).toUpperCase() + category.slice(1)
    };
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
          <p className="text-sm text-gray-500">Loading knowledge...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-600 text-sm">{error}</p>
          <button
            onClick={fetchContexts}
            className="mt-2 px-4 py-2 text-sm text-red-600 hover:bg-red-100 rounded-md"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-medium">AI Knowledge</h3>
          <p className="text-sm text-gray-500 mt-1">
            {contexts.length} knowledge files across {Object.keys(groupedContexts).length} categories
          </p>
        </div>
        <button
          onClick={fetchContexts}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh knowledge"
        >
          <RefreshCw className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      <div className="space-y-3">
        {Object.entries(groupedContexts).map(([category, files]) => {
          const config = getCategoryConfig(category);
          const CategoryIcon = config.icon;
          const isExpanded = expandedCategories[category];

          return (
            <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${config.color}`}>
                    <CategoryIcon className="h-5 w-5" />
                  </div>
                  <div className="text-left">
                    <span className="font-medium text-gray-900">{config.label}</span>
                    <span className="ml-2 text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                      {files.length} {files.length === 1 ? 'file' : 'files'}
                    </span>
                  </div>
                </div>
                {isExpanded ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </button>

              {/* Files List */}
              {isExpanded && (
                <div className="border-t border-gray-200 divide-y divide-gray-100">
                  {files.map((file, idx) => {
                    const fileKey = `${category}-${file.file}`;
                    const isFileExpanded = expandedFiles[fileKey];

                    return (
                      <div key={idx} className="bg-white">
                        <button
                          onClick={() => toggleFile(fileKey)}
                          className="w-full flex items-center justify-between p-3 pl-14 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <FileCode className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-700 font-mono">{file.file}</span>
                          </div>
                          {isFileExpanded ? (
                            <ChevronDown className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          )}
                        </button>

                        {/* File Content */}
                        {isFileExpanded && (
                          <div className="px-4 pb-4 pl-14">
                            <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                              <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
                                {file.content}
                              </pre>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {contexts.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <BookOpen className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No knowledge available</p>
        </div>
      )}
    </div>
  );
};

const menuItems = [
  { id: 'general', icon: Settings2, label: 'General' },
  { id: 'ai', icon: Terminal, label: 'AI Service' },
  { id: 'prompts', icon: FileText, label: 'Instructions' },
  { id: 'contexts', icon: BookOpen, label: 'Knowledge' },
  { id: 'history', icon: Clock, label: 'History' },
  { id: 'feedback', icon: MessageSquare, label: 'Feedback' },
  { id: 'training', icon: Sparkles, label: 'Training' },
  { id: 'debug', icon: Terminal, label: 'Config' }
];

const SettingsPopup = ({ isOpen, onClose, onClearHistory, onLogout, histories = [] }) => {
  const [activeSection, setActiveSection] = useState('general');
  const [settings, setSettings] = useState(() => ({
    apiToken: localStorage.getItem('apiToken') || '',
    theme: localStorage.getItem('theme') || 'light',
    showSqlResponse: localStorage.getItem('showSqlResponse') === 'true' || false,
    defaultOpenSection: localStorage.getItem('defaultOpenSection') || 'none',
    shareResponses: localStorage.getItem('shareResponses') === 'true',
    allowModelChanges: localStorage.getItem('allowModelChanges') === 'true',
    useAdvancedReasoning: localStorage.getItem('useAdvancedReasoning') === 'true' || localStorage.getItem('useAdvancedReasoning') === null,
    showJsonResponse: localStorage.getItem('showJsonResponse') === 'true' || false,
    showTableResponse: localStorage.getItem('showTableResponse') === 'true' || localStorage.getItem('showTableResponse') === null || true,
    showInsightsResponse: localStorage.getItem('showInsightsResponse') === 'true' || localStorage.getItem('showInsightsResponse') === null || true,
    showInvestigationsResponse: localStorage.getItem('showInvestigationsResponse') === 'true' || localStorage.getItem('showInvestigationsResponse') === null || true,
    showVisualizeData: localStorage.getItem('showVisualizeData') === 'true' || localStorage.getItem('showVisualizeData') === null || true,
    debug: localStorage.getItem('debug') === 'true' || false,
  }));
  const [showToken, setShowToken] = useState(false);
  const [errors, setErrors] = useState({});
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saveLikedChats, setSaveLikedChats] = useState(true);
  const [position, setPosition] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const dragRef = useRef(null)
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [clearOption, setClearOption] = useState('all');
  const dialogRef = useRef(null);
  const headerRef = useRef(null);

  const initializeDefaultSettings = () => {
    // Set default values in localStorage for settings that should default to true
    if (localStorage.getItem('showTableResponse') === null) {
      localStorage.setItem('showTableResponse', 'true');
    }
    
    if (localStorage.getItem('showInsightsResponse') === null) {
      localStorage.setItem('showInsightsResponse', 'true');
    }
    
    if (localStorage.getItem('showInvestigationsResponse') === null) {
      localStorage.setItem('showInvestigationsResponse', 'true');
    }
    
    if (localStorage.getItem('showVisualizeData') === null) {
      localStorage.setItem('showVisualizeData', 'true');
    }
    
    // Set default values for settings that should default to false
    if (localStorage.getItem('showJsonResponse') === null) {
      localStorage.setItem('showJsonResponse', 'false');
    }
    
    if (localStorage.getItem('showSqlResponse') === null) {
      localStorage.setItem('showSqlResponse', 'false');
    }
  };

  // Initialize default settings when component mounts
  useEffect(() => {
    initializeDefaultSettings();
  }, []);

  // Also initialize settings when login event is detected
  useEffect(() => {
    const handleLoginSuccess = () => {
      console.log('SettingsPopup: Detected login success, initializing default settings');
      initializeDefaultSettings();
    };
    
    window.addEventListener('loginSuccess', handleLoginSuccess);
    return () => {
      window.removeEventListener('loginSuccess', handleLoginSuccess);
    };
  }, []);

  const handleMouseDown = (e) => {
    if (e.clientY - dragRef.current.getBoundingClientRect().top < 50) {
      setIsDragging(true)
      const rect = dragRef.current.getBoundingClientRect()
      setDragStart({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      })
      // Capture current position when starting drag
      if (!position) {
        setPosition({
          x: rect.left,
          y: rect.top
        })
      }
      e.preventDefault()
    }
  }

  const handleMouseMove = (e) => {
    if (isDragging) {
      const newX = e.clientX - dragStart.x
      const newY = e.clientY - dragStart.y
      setPosition({ x: newX, y: newY })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleClose = () => {
    setPosition(null)
    onClose()
  }

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, dragStart])

  // Add event listener to sync settings with localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      // Get current values from localStorage
      const storedToken = localStorage.getItem('apiToken');

      // Update settings state if values have changed
      setSettings(prevSettings => {
        if (storedToken && storedToken !== prevSettings.apiToken) {
          console.log('SettingsPopup: apiToken updated from localStorage');
          return { ...prevSettings, apiToken: storedToken };
        }
        return prevSettings;
      });
    };

    // Listen for the storage event (when localStorage changes)
    window.addEventListener('storage', handleStorageChange);

    // Create a custom event listener for login events
    const handleLoginSuccess = () => {
      handleStorageChange();
      console.log('SettingsPopup: Detected login success, refreshing settings');
    };

    window.addEventListener('loginSuccess', handleLoginSuccess);

    // Also check for updates on a timer to catch localStorage changes that don't fire storage events
    // (for example, when localStorage is changed within the same window)
    const intervalId = setInterval(handleStorageChange, 2000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('loginSuccess', handleLoginSuccess);
      clearInterval(intervalId);
    };
  }, []);

  // Theme handling
  useEffect(() => {
    const handleTheme = () => {
      const theme = settings.theme;
      const root = window.document.documentElement;
      
      root.classList.remove('light', 'dark');
      
      if (theme === 'system') {
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        root.classList.add(systemTheme);
      } else {
        root.classList.add(theme);
      }
      
      localStorage.setItem('theme', theme);
    };

    handleTheme();
  }, [settings.theme]);

  // Validate and save API token
  const validateAndSaveAPISettings = async (value) => {
    const newErrors = { ...errors };

    if (value.length < 10) {
      newErrors.apiToken = 'API Token must be at least 10 characters long';
    } else {
      delete newErrors.apiToken;
      // Update the apiService singleton with the new token
      apiService.updateSettings(value);
    }

    setErrors(newErrors);
    setSettings(prev => ({ ...prev, apiToken: value }));
    localStorage.setItem('apiToken', value);
  };

  // Test API connection
  const testConnection = async () => {
    setIsTesting(true);
    setTestResult(null);

    try {
      // Save token to localStorage before testing
      localStorage.setItem('apiToken', settings.apiToken);

      // Update the API service with current token
      const updateResult = apiService.updateSettings(settings.apiToken);

      if (!updateResult.success) {
        throw new Error(`Settings validation failed: ${updateResult.error}`);
      }

      const healthData = await apiService.checkHealth();

      setTestResult({
        success: true,
        message: `Connection successful! API version: ${healthData.version || 'unknown'}`,
        data: healthData
      });

      // Also test thread creation
      try {
        const threadResponse = await apiService.createThread();

        const threadId = threadResponse.id || threadResponse.threadId;
        if (threadId) {
          setTestResult(prev => ({
            ...prev,
            message: `${prev.message}. Thread creation successful!`,
            threadCreated: true
          }));
        } else {
          setTestResult(prev => ({
            ...prev,
            message: `${prev.message}. Thread creation returned unexpected format.`,
            threadCreated: false,
            threadResponse
          }));
        }
      } catch (threadError) {
        setTestResult(prev => ({
          ...prev,
          message: `${prev.message}. Thread creation failed: ${threadError.message}`,
          threadCreated: false
        }));
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: `Connection failed: ${error.message}`,
        error: error
      });
    } finally {
      setIsTesting(false);
    }
  };

  // Format token display
  const formatToken = (token) => {
    if (!token) return '';
    if (showToken) return token;
    if (token.length <= 4) return '*'.repeat(token.length);
    return '*'.repeat(token.length - 4) + token.slice(-4);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClearHistory = (keepLiked = false) => {
    if (keepLiked) {
      // Filter liked histories from the prop
      const likedHistories = histories.filter(chat => chat.isLiked === true);

      // Update parent components - let the parent handle the API deletion
      onClearHistory(likedHistories);

      // Notify other components
      window.dispatchEvent(new CustomEvent('chatHistoryUpdated', {
        detail: { chats: likedHistories }
      }));
    } else {
      // Let parent handle clearing all histories via API
      onClearHistory([]);

      // Notify other components
      window.dispatchEvent(new CustomEvent('chatHistoryUpdated', {
        detail: { chats: [] }
      }));
    }

    // Close settings
    onClose();
  };

  const handleShareResponsesChange = (e) => {
    const newValue = e.target.checked;
    setSettings(prev => ({ ...prev, shareResponses: newValue }));
    localStorage.setItem('shareResponses', String(newValue));
  };

  const handleClearAllData = () => {
    localStorage.clear();
    setShowClearConfirmation(false);
    // Optionally refresh the page to reset all app state
    window.location.reload();
  };

  const handleAllowModelChangesChange = (e) => {
    const newValue = e.target.checked;
    setSettings(prev => ({ ...prev, allowModelChanges: newValue }));
    localStorage.setItem('allowModelChanges', String(newValue));
  };

  const renderClearConfirmationDialog = () => (
    <Transition show={showClearConfirmation} as={Fragment}>
      <Dialog 
        as="div" 
        className="relative z-[1002]"
        onClose={() => setShowClearConfirmation(false)}
      >
        <div className="fixed inset-0 bg-black/70" />
        
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all" style={{ zIndex: 1003 }}>
              <Dialog.Title as="h3" className="text-lg font-medium text-gray-900 mb-4">
                Clear All Data?
              </Dialog.Title>
              
              <div className="mb-6">
                <p className="text-sm text-gray-500">
                  This will permanently delete all your data, including:
                </p>
                <ul className="mt-2 text-sm text-gray-500 list-disc list-inside">
                  <li>All chat history</li>
                  <li>All custom prompts</li>
                  <li>All settings and preferences</li>
                  <li>API configuration</li>
                </ul>
                <p className="mt-2 text-sm text-gray-500">
                  This action cannot be undone.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md border border-gray-300"
                  onClick={() => setShowClearConfirmation(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-md"
                  onClick={handleClearAllData}
                >
                  Clear All Data
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    </Transition>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'general':
        return (
          <div className="p-6">
            <h3 className="text-lg font-medium mb-6">General Settings</h3>
            
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Allow Model Changes</label>
                  <p className="text-xs text-gray-500 mt-1">
                    Enable model selection in chat interface
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.allowModelChanges}
                    onChange={handleAllowModelChangesChange}
                  />
                  <div className={`
                    w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4
                    peer-focus:ring-gray-300 rounded-full peer
                    peer-checked:after:translate-x-full peer-checked:after:border-white
                    after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                    after:bg-white after:border-gray-300 after:border after:rounded-full
                    after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600
                  `}></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Share Responses</label>
                  <p className="text-xs text-gray-500 mt-1">
                    Help improve TrackVision by sharing anonymized responses
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.shareResponses}
                    onChange={handleShareResponsesChange}
                  />
                  <div className={`
                    w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4
                    peer-focus:ring-gray-300 rounded-full peer
                    peer-checked:after:translate-x-full peer-checked:after:border-white
                    after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                    after:bg-white after:border-gray-300 after:border after:rounded-full
                    after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600
                  `}></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Debug Mode</label>
                  <p className="text-xs text-gray-500 mt-1">
                    Enable debug features and logging
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.debug}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setSettings(prev => ({ ...prev, debug: newValue }));
                      localStorage.setItem('debug', String(newValue));
                      // Optionally dispatch an event if other components need to know
                      // const event = new CustomEvent('settingsChanged', { detail: { key: 'debug', value: newValue } });
                      // window.dispatchEvent(event);
                    }}
                  />
                  <div className={`
                    w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4
                    peer-focus:ring-gray-300 rounded-full peer
                    peer-checked:after:translate-x-full peer-checked:after:border-white
                    after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                    after:bg-white after:border-gray-300 after:border after:rounded-full
                    after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600
                  `}></div>
                </label>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setShowClearConfirmation(true)}
                  className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-md"
                >
                  Clear All Data
                </button>
              </div>
            </div>
          </div>
        );

      case 'ai':
        return (
          <div className="p-6">
            <h3 className="text-lg font-medium mb-6">AI Service Settings</h3>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">API Key</label>
                <p className="text-xs text-gray-500 mb-2">
                  Enter your API key to access the AI service
                </p>
                <div className="relative">
                  <input
                    type={showToken ? 'text' : 'password'}
                    value={settings.apiToken}
                    onChange={(e) => validateAndSaveAPISettings(e.target.value)}
                    placeholder="Enter API key"
                    className={`w-full px-3 py-1.5 text-sm rounded-md border ${
                      errors.apiToken ? 'border-red-500' : 'border-gray-300'
                    } focus:ring-1 focus:ring-gray-500 focus:outline-none pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowToken(!showToken)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showToken ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.apiToken && (
                  <p className="mt-1 text-xs text-red-500">{errors.apiToken}</p>
                )}
                {!showToken && settings.apiToken && (
                  <p className="mt-1 text-sm font-mono text-gray-600">
                    {formatToken(settings.apiToken)}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Use Advanced Reasoning</label>
                  <p className="text-xs text-gray-500 mt-1">
                    Enable more complex reasoning capabilities (may be slower)
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.useAdvancedReasoning}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setSettings(prev => ({ ...prev, useAdvancedReasoning: newValue }));
                      localStorage.setItem('useAdvancedReasoning', String(newValue));
                      
                      // Dispatch a custom event to notify other components
                      const event = new CustomEvent('settingsChanged', { 
                        detail: { key: 'useAdvancedReasoning', value: newValue } 
                      });
                      window.dispatchEvent(event);
                    }}
                  />
                  <div className={`
                    w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4
                    peer-focus:ring-gray-300 rounded-full peer
                    peer-checked:after:translate-x-full peer-checked:after:border-white
                    after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                    after:bg-white after:border-gray-300 after:border after:rounded-full
                    after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600
                  `}></div>
                </label>
              </div>

              <div className="mt-6">
                <button
                  onClick={testConnection}
                  disabled={isTesting || Object.keys(errors).length > 0}
                  className={`w-full px-4 py-2 text-sm rounded-md ${
                    isTesting
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  {isTesting ? 'Testing...' : 'Test Connection'}
                </button>
                
                {testResult && (
                  <div className={`mt-2 p-2 rounded-md text-sm ${
                    testResult.success 
                      ? 'bg-green-50 text-green-600'
                      : 'bg-red-50 text-red-600'
                  }`}>
                    {testResult.message}
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'prompts':
        return <PromptsSection />

      case 'contexts':
        return <ContextsSection />

      case 'training':
        return <TrainingSection />

      case 'history':
        return (
          <div className="p-6">
            <h3 className="text-lg font-medium mb-6">Chat History</h3>
            
            <div className="space-y-6">
              <p className="text-sm text-gray-600 mb-4">
                Clear your chat history. This action cannot be undone.
              </p>
              
              <div className="flex items-center space-x-2 mb-4">
                <input
                  type="checkbox"
                  id="saveLikedChats"
                  checked={saveLikedChats}
                  onChange={(e) => setSaveLikedChats(e.target.checked)}
                  className="rounded border-gray-300 text-gray-600 focus:ring-gray-500"
                />
                <label htmlFor="saveLikedChats" className="text-sm text-gray-600">
                  Keep liked chats
                </label>
              </div>

              <button
                onClick={() => handleClearHistory(saveLikedChats)}
                className="w-full px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
              >
                Delete Chat History
              </button>
            </div>
          </div>
        );

      case 'feedback':
        return <FeedbackSection />;

      case 'debug':
        return (
          <div className="p-6">
            <h3 className="text-lg font-medium mb-6">Config Settings</h3>
            
            <div className="space-y-6">
              {/* Display Options */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-700">Display Options</h4>
                
                {[
                  { key: 'showJsonResponse', label: 'JSON Response', desc: 'Display raw JSON responses in the chat window' },
                  { key: 'showSqlResponse', label: 'SQL Query', desc: 'Display formatted SQL queries in the chat window' },
                  { key: 'showTableResponse', label: 'Results', desc: 'Display results in table format' },
                  { key: 'showInsightsResponse', label: 'Insights', desc: 'Display AI-generated insights' },
                  { key: 'showInvestigationsResponse', label: 'Investigations', desc: 'Display suggested investigations' },
                  { key: 'showVisualizeData', label: 'Visualize Data', desc: 'Display data visualization options' }
                ].map(({ key, label, desc }) => (
                  <div key={key} className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium">{label}</label>
                      <p className="text-xs text-gray-500 mt-1">{desc}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={settings[key]}
                        onChange={(e) => {
                          const newValue = e.target.checked;
                          setSettings(prev => ({ ...prev, [key]: newValue }));
                          localStorage.setItem(key, String(newValue));
                          
                          // Dispatch a custom event to notify other components
                          const event = new CustomEvent('settingsChanged', { 
                            detail: { key, value: newValue } 
                          });
                          window.dispatchEvent(event);
                        }}
                      />
                      <div className={`
                        w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 
                        peer-focus:ring-blue-300 rounded-full peer 
                        peer-checked:after:translate-x-full peer-checked:after:border-white 
                        after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                        after:bg-white after:border-gray-300 after:border after:rounded-full 
                        after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600
                      `}></div>
                    </label>
                  </div>
                ))}
              </div>

              {/* Default Open Section */}
              <div className="mt-8">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Default Open Section</h4>
                <p className="text-xs text-gray-500 mb-3">Choose which section should be open by default in chat responses</p>
                
                <select
                  value={settings.defaultOpenSection}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setSettings(prev => ({ ...prev, defaultOpenSection: newValue }));
                    localStorage.setItem('defaultOpenSection', newValue);
                    const event = new CustomEvent('settingsChanged', {
                      detail: { key: 'defaultOpenSection', value: newValue }
                    });
                    window.dispatchEvent(event);
                  }}
                  className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-1 focus:ring-gray-500 focus:outline-none"
                >
                  <option value="none">None</option>
                  <option value="json">JSON Response</option>
                  <option value="sql">SQL Query</option>
                  <option value="table">Table</option>
                  <option value="insights">Insights</option>
                  <option value="investigations">Investigations</option>
                  <option value="chart">Visualize Data</option>
                </select>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[1000]"
        onClick={handleBackdropClick}
      >
        <div 
          ref={dragRef}
          className="bg-white rounded-lg shadow-xl flex max-h-[90vh] w-[1000px]"
          onClick={e => e.stopPropagation()}
          style={{ 
            position: 'fixed',
            ...(!position ? {
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)'
            } : {
              left: 0,
              top: 0,
              transform: `translate(${position.x}px, ${position.y}px)`
            }),
            cursor: isDragging ? 'grabbing' : 'default',
            transition: isDragging ? 'none' : 'transform 0.1s',
            zIndex: 1001
          }}
        >
          {/* Draggable header area */}
          <div 
            className="absolute inset-x-0 top-0 h-12 cursor-move"
            onMouseDown={handleMouseDown}
            style={{ zIndex: 10 }}
          />

          {/* Sidebar Menu - Fixed width */}
          <div className="w-64 bg-gray-100 border-r border-gray-300 flex-shrink-0">
            <div className="p-4 border-b border-gray-300 bg-gray-200">
              <h2 className="text-xl font-semibold select-none text-gray-900">Settings</h2>
            </div>
            <nav className="p-2">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm ${
                    activeSection === item.id
                      ? 'bg-gray-600 text-white shadow-sm'
                      : 'text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Content Area */}
          <div className="flex-1 flex flex-col min-h-[600px] max-h-[80vh] relative">
            <button
              onClick={handleClose}
              className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 z-20"
            >
              <X className="h-6 w-6" />
            </button>
            <div className="flex-1 overflow-y-auto pt-12">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
      
      {/* Clear confirmation dialog */}
      {renderClearConfirmationDialog()}
    </>
  );
};

export default SettingsPopup; 